import React, { useState, useEffect, useRef } from 'react';
import styles from './StoryModal.module.css';

const StoryModal = ({ story, onClose, onPrev, onNext, hasNext, hasPrev }) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress]                   = useState(0);
  const [isPaused, setIsPaused]                   = useState(false);
  const videoRef = useRef(null);

  const currentSlide  = story.stories[currentStoryIndex];
  const totalSlides   = story.stories.length;
  const DURATION      = currentSlide?.duration || 5000;

  // Reset index when story (user) changes
  useEffect(() => {
    setCurrentStoryIndex(0);
    setProgress(0);
  }, [story.id]);

  // Progress bar ticker
  useEffect(() => {
    if (isPaused || !currentSlide) return;

    setProgress(0);
    const interval  = 50;
    const increment = (interval / DURATION) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Advance within this user's slides, or go to next user
          if (currentStoryIndex < totalSlides - 1) {
            setCurrentStoryIndex(i => i + 1);
            return 0;
          } else {
            if (hasNext) onNext();
            else onClose();
            return 0;
          }
        }
        return prev + increment;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [currentStoryIndex, isPaused, DURATION, totalSlides, hasNext, onNext, onClose]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  goBack();
      if (e.key === 'ArrowRight') goForward();
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentStoryIndex, hasPrev, hasNext]);

  const goBack = () => {
    if (currentStoryIndex > 0) { setCurrentStoryIndex(i => i - 1); setProgress(0); }
    else if (hasPrev) onPrev();
  };

  const goForward = () => {
    if (currentStoryIndex < totalSlides - 1) { setCurrentStoryIndex(i => i + 1); setProgress(0); }
    else if (hasNext) onNext();
    else onClose();
  };

  if (!currentSlide) return null;

  // Format relative time
  const formatAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor(diff / 60000);
    if (h >= 1) return `${h}h ago`;
    if (m >= 1) return `${m}m ago`;
    return 'just now';
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className={styles.storyHeader}>
          {/* Progress bars */}
          <div className={styles.progressBars}>
            {story.stories.map((_, i) => (
              <div key={i} className={styles.progressBarContainer}>
                <div
                  className={styles.progressBar}
                  style={{
                    width: i < currentStoryIndex ? '100%' : i === currentStoryIndex ? `${progress}%` : '0%',
                  }}
                />
              </div>
            ))}
          </div>

          {/* User info */}
          <div className={styles.userInfo}>
            <img
              src={story.avatar}
              alt={story.username}
              className={styles.userAvatar}
              onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(story.username)}&background=25D366&color=fff`; }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span className={styles.username}>{story.username}</span>
              <span className={styles.timeAgo}>{formatAgo(currentSlide.expires_at ? new Date(currentSlide.expires_at).getTime() - 86400000 : null)}</span>
            </div>
          </div>

          {/* Close */}
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* ── Media ── */}
        <div
          className={styles.storyContent}
          onMouseDown={() => setIsPaused(true)}
          onMouseUp={() => setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => setIsPaused(false)}
        >
          {currentSlide.type === 'video' ? (
            <video
              ref={videoRef}
              key={currentSlide.id}
              src={currentSlide.url}
              className={styles.storyMedia}
              autoPlay
              playsInline
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <img
              key={currentSlide.id}
              src={currentSlide.url}
              alt="Status"
              className={styles.storyMedia}
              style={{ objectFit: 'contain' }}
            />
          )}

          {/* Tap zones */}
          <div className={styles.navLeft}  onClick={e => { e.stopPropagation(); goBack(); }} />
          <div className={styles.navRight} onClick={e => { e.stopPropagation(); goForward(); }} />

          {/* Caption */}
          {currentSlide.caption && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
              padding: '48px 20px 20px',
              color: '#fff', fontSize: 15, lineHeight: 1.5,
              pointerEvents: 'none',
            }}>
              {currentSlide.caption}
            </div>
          )}

          {/* View count (shown when it's own story and view_count exists) */}
          {currentSlide.view_count !== undefined && (
            <div style={{
              position: 'absolute',
              bottom: currentSlide.caption ? 88 : 16,
              right: 16,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 4,
              pointerEvents: 'none',
            }}>
              👁 {currentSlide.view_count}
            </div>
          )}
        </div>

        {/* ── Prev / Next arrows ── */}
        {hasPrev && (
          <button className={styles.navArrowLeft} onClick={e => { e.stopPropagation(); onPrev(); }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        )}
        {hasNext && (
          <button className={styles.navArrowRight} onClick={e => { e.stopPropagation(); onNext(); }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default StoryModal;

















// import React, { useState, useEffect } from 'react';
// import styles from './StoryModal.module.css';

// const StoryModal = ({ story, onClose, onPrev, onNext, hasNext, hasPrev }) => {
//   const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
//   const [progress, setProgress] = useState(0);
//   const [isPaused, setIsPaused] = useState(false);

//   const currentStory = story.stories[currentStoryIndex];
//   const totalStories = story.stories.length;

//   // ✅ Progress bar animation
//   useEffect(() => {
//     if (isPaused) return;

//     const duration = currentStory.duration || 5000;
//     const interval = 50; // Update every 50ms
//     const increment = (interval / duration) * 100;

//     const timer = setInterval(() => {
//       setProgress(prev => {
//         if (prev >= 100) {
//           // Move to next story in this user's stories
//           if (currentStoryIndex < totalStories - 1) {
//             setCurrentStoryIndex(currentStoryIndex + 1);
//             return 0;
//           } else {
//             // Move to next user's stories
//             onNext();
//             return 0;
//           }
//         }
//         return prev + increment;
//       });
//     }, interval);

//     return () => clearInterval(timer);
//   }, [currentStoryIndex, currentStory, isPaused, totalStories, onNext]);

//   // ✅ Keyboard navigation
//   useEffect(() => {
//     const handleKeyPress = (e) => {
//       if (e.key === 'ArrowLeft' && hasPrev) onPrev();
//       if (e.key === 'ArrowRight' && hasNext) onNext();
//       if (e.key === 'Escape') onClose();
//     };

//     window.addEventListener('keydown', handleKeyPress);
//     return () => window.removeEventListener('keydown', handleKeyPress);
//   }, [hasPrev, hasNext, onPrev, onNext, onClose]);

//   const handlePrevStory = () => {
//     if (currentStoryIndex > 0) {
//       setCurrentStoryIndex(currentStoryIndex - 1);
//       setProgress(0);
//     } else if (hasPrev) {
//       onPrev();
//     }
//   };

//   const handleNextStory = () => {
//     if (currentStoryIndex < totalStories - 1) {
//       setCurrentStoryIndex(currentStoryIndex + 1);
//       setProgress(0);
//     } else if (hasNext) {
//       onNext();
//     } else {
//       onClose();
//     }
//   };

//   return (
//     <div className={styles.modalOverlay} onClick={onClose}>
//       <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        
//         {/* Header */}
//         <div className={styles.storyHeader}>
//           {/* Progress bars */}
//           <div className={styles.progressBars}>
//             {story.stories.map((_, index) => (
//               <div key={index} className={styles.progressBarContainer}>
//                 <div 
//                   className={styles.progressBar}
//                   style={{
//                     width: index < currentStoryIndex ? '100%' : 
//                            index === currentStoryIndex ? `${progress}%` : '0%'
//                   }}
//                 />
//               </div>
//             ))}
//           </div>

//           {/* User info */}
//           <div className={styles.userInfo}>
//             <img src={story.avatar} alt={story.username} className={styles.userAvatar} />
//             <span className={styles.username}>{story.username}</span>
//             <span className={styles.timeAgo}>2h</span>
//           </div>

//           {/* Close button */}
//           <button className={styles.closeBtn} onClick={onClose}>
//             ×
//           </button>
//         </div>

//         {/* Story Content */}
//         <div 
//           className={styles.storyContent}
//           onMouseDown={() => setIsPaused(true)}
//           onMouseUp={() => setIsPaused(false)}
//           onTouchStart={() => setIsPaused(true)}
//           onTouchEnd={() => setIsPaused(false)}
//         >
//           {currentStory.type === 'image' ? (
//             <img 
//               src={currentStory.url} 
//               alt="Story"
//               className={styles.storyMedia}
//             />
//           ) : (
//             <video 
//               src={currentStory.url}
//               className={styles.storyMedia}
//               autoPlay
//               muted
//               playsInline
//             />
//           )}

//           {/* Navigation Areas */}
//           <div className={styles.navLeft} onClick={handlePrevStory} />
//           <div className={styles.navRight} onClick={handleNextStory} />
//         </div>

//         {/* Navigation Arrows */}
//         {hasPrev && (
//           <button className={styles.navArrowLeft} onClick={onPrev}>
//             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//               <path d="M15 18l-6-6 6-6"/>
//             </svg>
//           </button>
//         )}
//         {hasNext && (
//           <button className={styles.navArrowRight} onClick={onNext}>
//             <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//               <path d="M9 18l6-6-6-6"/>
//             </svg>
//           </button>
//         )}
//       </div>
//     </div>
//   );
// };

// export default StoryModal;
